import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { TaxReason } from 'src/features/taxReasons/utils/types';
import { useDeleteTaxReason } from 'src/features/taxReasons/api/useDeleteTaxReason';
import { LIST_TAX_REASONS_RESOURCE } from 'src/features/taxReasons/api/useListTaxReasons';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteTaxReasonFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTaxReason: TaxReason;
    children?: React.ReactNode;
}

function DeleteTaxReasonFormModal<TData>({ selectedTaxReason, children }: DeleteTaxReasonFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const { mutate: deletePayStation } = useDeleteTaxReason({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_TAX_REASONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(taxReasonsMessages.deleteTaxReason),
                description: formatMessage(taxReasonsMessages.deleteTaxReasonSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...taxReasonsMessages.deleteTaxReason },
                subTitle: {
                    ...taxReasonsMessages.deleteTaxReasonSubtitle,
                    values: {
                        entity: selectedTaxReason.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...taxReasonsMessages.confirmDeleteTaxReason },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: taxReasonsMessages.deleteTaxReason,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedTaxReason.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteTaxReasonFormModal;
